import React from 'react'

import { createLucideIcon, LucideIcon } from 'lucide-react'
import { computed } from 'mobx'

import {
  getWorkflowTypeGlyph,
  resolveGlyphName,
} from '~/client/src/shared/constants/workflowTypeGlyphs'

import Localization from '../../localization/LocalizationManager'

// Memoized, and on the RESOLVED name: createLucideIcon returns a new component type per call,
// and reads the name eagerly — so a nullish one from an optional caller throws during render.
const glyphIcons = new Map<string, LucideIcon>()

export function workflowGlyphIcon(iconName: string): LucideIcon {
  const name = resolveGlyphName(iconName)
  let icon = glyphIcons.get(name)
  if (!icon) {
    icon = createLucideIcon(name, getWorkflowTypeGlyph(name))
    glyphIcons.set(name, icon)
  }
  return icon
}

// For a caller holding a name rather than an icon.
export const WorkflowGlyph = ({
  iconName,
  size,
  color,
}: {
  iconName?: string
  size?: number
  color?: string
}) => {
  const Glyph = workflowGlyphIcon(iconName)
  return <Glyph size={size} color={color} />
}

// The generic Workflow mark, and the product's only definition of it.
export const GenericWorkflowIcon = workflowGlyphIcon('general-workflow')

const WorkflowIconGroups = computed(() => [
  {
    desc: Localization.translator.general,
    icons: [
      {
        name: 'general-workflow',
        desc: Localization.translator.generalWorkflow,
        component: GenericWorkflowIcon,
      },
    ],
  },
  {
    desc: Localization.translator.automation,
    icons: [
      {
        name: 'automation',
        desc: Localization.translator.automation,
        component: workflowGlyphIcon('automation'),
      },
    ],
  },
  {
    desc: Localization.translator.delivery,
    icons: [
      {
        name: 'crane',
        desc: Localization.translator.craneBooking,
        component: workflowGlyphIcon('crane'),
      },
      {
        name: 'delivery',
        desc: Localization.translator.deliveryBooking,
        component: workflowGlyphIcon('delivery'),
      },
    ],
  },
  {
    desc: Localization.translator.environmental,
    icons: [
      {
        name: 'air-mitigation',
        desc: Localization.translator.airMitigation,
        component: workflowGlyphIcon('air-mitigation'),
      },
      {
        name: 'eco',
        desc: Localization.translator.eco,
        component: workflowGlyphIcon('eco'),
      },
    ],
  },
  {
    desc: Localization.translator.equipment,
    icons: [
      {
        name: 'vehicle-access',
        desc: Localization.translator.authorizedVehicleAccess,
        component: workflowGlyphIcon('vehicle-access'),
      },
      {
        name: 'fire-extinguisher',
        desc: Localization.translator.fireExtinguisherPermit,
        component: workflowGlyphIcon('fire-extinguisher'),
      },
      {
        name: 'heavy-equipment',
        desc: Localization.translator.heavyEquipmentUsageLog,
        component: workflowGlyphIcon('heavy-equipment'),
      },
      {
        name: 'ladder',
        desc: Localization.translator.ladderPermit,
        component: workflowGlyphIcon('ladder'),
      },
      {
        name: 'aerial-inspection',
        desc: Localization.translator.aerialLiftInspection,
        component: workflowGlyphIcon('aerial-inspection'),
      },
      {
        name: 'forklift',
        desc: Localization.translator.forkliftRoomWorkPermit,
        component: workflowGlyphIcon('forklift'),
      },
    ],
  },
  {
    desc: Localization.translator.material,
    icons: [
      {
        name: 'material-transfer',
        desc: Localization.translator.materialTransfer,
        component: workflowGlyphIcon('material-transfer'),
      },
      {
        name: 'procurement-tracking',
        desc: Localization.translator.procurementTracking,
        component: workflowGlyphIcon('procurement-tracking'),
      },
    ],
  },
  {
    desc: Localization.translator.quality,
    icons: [
      {
        name: 'quality-control',
        desc: Localization.translator.qualityControlInspection,
        component: workflowGlyphIcon('quality-control'),
      },
    ],
  },
  {
    desc: Localization.translator.report,
    icons: [
      {
        name: 'report',
        desc: Localization.translator.report,
        component: workflowGlyphIcon('report'),
      },
      {
        name: 'timesheet',
        desc: Localization.translator.timesheet,
        component: workflowGlyphIcon('timesheet'),
      },
    ],
  },
  {
    desc: Localization.translator.safety,
    icons: [
      {
        name: 'energized',
        desc: Localization.translator.energizedRoomWorkPermit,
        component: workflowGlyphIcon('energized'),
      },
      {
        name: 'hotwork',
        desc: Localization.translator.hotWorkPermit,
        component: workflowGlyphIcon('hotwork'),
      },
      {
        name: 'hazard',
        desc: Localization.translator.jobHazardAnalysis,
        component: workflowGlyphIcon('hazard'),
      },
      {
        name: 'surface-penetration',
        desc: Localization.translator.surfacePenetrationPermit,
        component: workflowGlyphIcon('surface-penetration'),
      },
      {
        name: 'concrete-penetration',
        desc: Localization.translator.interiorConcretePenetrationPermit,
        component: workflowGlyphIcon('concrete-penetration'),
      },
      {
        name: 'controlled-access-zone',
        desc: Localization.translator.controlledAccessZone,
        component: workflowGlyphIcon('controlled-access-zone'),
      },
      {
        name: 'guardrail-removal',
        desc: Localization.translator.guardrailRemovalPermit,
        component: workflowGlyphIcon('guardrail-removal'),
      },
      {
        name: 'roof-access',
        desc: Localization.translator.roofAccessPermit,
        component: workflowGlyphIcon('roof-access'),
      },
    ],
  },
  {
    desc: Localization.translator.siteCommunication,
    icons: [
      {
        name: 'closure',
        desc: Localization.translator.closureRequest,
        component: workflowGlyphIcon('closure'),
      },
      {
        name: 'announcement',
        desc: Localization.translator.siteAnnouncement,
        component: workflowGlyphIcon('announcement'),
      },
    ],
  },
])

export default WorkflowIconGroups
